import React, {
	createContext,
	FC,
	ReactNode,
	useEffect,
	useMemo,
	useState,
	useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
	openDatabase,
	storeUsers,
	getAllUsers,
	storeCurrencyPolicies,
	removeAllUsers,
	removeCurrencyPolicies,
} from './indexdbConfig';


interface IPublicSettingsProps {
	"id": number;
	"key": string;
	"value": boolean | string | number;
}


interface Video {
	_id: number;
	title: string;
	url: string;
	thumbnail: string;
	type: string;
	description: string;
}



export interface IEducationCenterProps {
	playlists?: Video;
}

export interface IUserProps {
	accountId?: number;
	accountIdPrefix?: string;
	closeOnly?: boolean;
	creditLoanPercentage?: number;
	currencyPolicyId?: number;
	currencySign?: string;
	demo?: boolean;
	enableCashDelivery?: boolean;
	firstName?: string;
	genericPolicyId?: number;
	id?: number;
	lastName?: string;
	locked?: boolean;
	
	parentId?: number;
	percentageLevel1?: number;
	percentageLevel2?: number;
	percentageLevel3?: number;
	percentageLevel4?: number;
	tradingType?: number;
	userCurrencyId?: number;
	userName?: string;
	userType?: number;
	whiteLabel?: boolean;
	gmtOffset?: string;
	serverGmtOffset?: string;
}
interface ICurrencyPolicyProps {
	requiredMarginTypeId?: number;
	openCommissionTypeId?: number;
	closedCommissionTypeId?: number;
	intraDayCommissionValue?: number;
	requiredMargin?: number;
	sellRequiredMarginType?: number;
	sellRequiredMargin?: number;
	buyHoldingMarginType?: number;
	buyHoldingMargin?: number;
	sellHoldingMarginType?: number;
	sellHoldingMargin?: number;
	spreadBalance?: number;
	spread?: number;
	entryAutoBroker?: number;
	limitOffset?: number;
	marketAutoBroker?: number;
	maximumAmountPerDeal?: number;
	maximumQuantity?: number;
	maximumValue?: number;
	percentage?: number;
	stopOffset?: number;
	intraDayCommissionTypeId?: number;
	sellOnly?: boolean;
	buyOnly?: boolean;
	limitOffsetTypeId?: number;
	stopOffsetTypeId?: number;
	minimumAmountPerDeal?: number;
	currencyId?: number;
	closedCommissionValue?: number;
	openCommissionValue?: number;
	visible?: boolean;
	spreadTypeId?: number;
}
export interface ICurrencyProps {
	amountUnitId?: number;
	autoCancelEntryOrders?: boolean;
	autoCancelSltpOrders?: boolean;
	autoStopTrade?: boolean;
	autoStopTradeSeconds?: number;
	buyOnly?: boolean;
	closeCancelAllOnExpiry?: boolean;
	closeDay?: number;
	closeTime?: string;
	closed?: boolean;
	contractSize?: number;
	currencyTypeId?: number;
	dailyCloseTimeFrom1?: string;
	dailyCloseTimeFrom2?: string;
	dailyCloseTimeFrom3?: string;
	dailyCloseTimeTo1?: string;
	dailyCloseTimeTo2?: string;
	dailyCloseTimeTo3?: string;
	decimalDigits?: number;
	description?: string;
	directCalculation?: boolean;
	expiryDate?: string;
	feederName?: string;
	id?: number;
	moveIfClosed?: boolean;
	name?: string;
	openDay?: number;
	openTime?: string;
	parentId?: number;
	refDirectCalculation?: boolean;
	referenceCurrencyId?: number;
	requotable?: boolean;
	rowColor?: string;
	sellOnly?: boolean;
	spreadFromBid?: boolean;
	tickDigits?: number;
	used?: boolean;
}

export interface IBanksProps {
	details: string;
	bankImageUrl: string;
	name: string;
	id: number;
}

export interface IConfigContextProps {
	usersHMap: any;
	publicSettings: Partial<IPublicSettingsProps>;
	setPublicSettingsData(...args: unknown[]): unknown;
	users: any;
	setUsersData(...args: unknown[]): unknown;
	banks: any;
	setBanksData(...args: unknown[]): unknown;
	currencies: any;
	setCurrenciesData(...args: unknown[]): unknown;
	currencyPolicies: any;
	setCurrencyPolicyDetails(...args: unknown[]): unknown;
}

const ConfigContext = createContext<IConfigContextProps>({} as IConfigContextProps);

interface IConfigContextProviderProps {
	children: ReactNode;
}

export const ConfigContextProvider: FC<IConfigContextProviderProps> = ({ children }) => {
	const [publicSettings, setPublicSettings] = useState<Partial<IPublicSettingsProps>>(
		JSON.parse(localStorage.getItem('ark_public_settings') || '{}'),
	);
	const [users, setUsers] = useState<Partial<IUserProps>[]>([]);
	const [currencyPolicies, setCurrencyPolicies] = useState<{
		[key: number]: ICurrencyPolicyProps;
	}>({});
	const [usersHMap, setUsersHMap] = useState({});
	const [banks, setBanks] = useState<Partial<IBanksProps>>(
		JSON.parse(localStorage.getItem('ark_banks') || '[{}]'),
	);
	const [currencies, setCurrencies] = useState<Partial<ICurrencyProps>>(
		JSON.parse(localStorage.getItem('ark_currencies') || '[{}]'),
	);
	const [db, setDb] = useState<any>(null);

	useEffect(() => {
		(async () => {
			try {
				const dbint = await openDatabase();
				setDb(dbint);
				const tempUsers = await getAllUsers(dbint);
				const userMap = new Map();
				for (const user of tempUsers) {
					userMap.set(user.id, user);
				}
				setUsersHMap(userMap);
			} catch (error) {
				console.error('Error initializing database:', error);
			}
		})();
	}, []);

	useEffect(() => {
		localStorage.setItem('ark_public_settings', JSON.stringify(publicSettings));
		storeUsers(db, users);
		const updatedCurrencyPolicies = Object.values(currencyPolicies);
		if (updatedCurrencyPolicies.length > 0) {
			storeCurrencyPolicies(db, updatedCurrencyPolicies);
		}
		localStorage.setItem('ark_banks', JSON.stringify(banks));
		localStorage.setItem('ark_currencies', JSON.stringify(currencies));
	}, [users, banks, currencies, currencyPolicies, db, publicSettings]);

	const setPublicSettingsData = (data: any) => {
		const publicSettingsData = JSON.stringify(data);
		if (publicSettingsData !== '') {
			setPublicSettings(data);
			localStorage.setItem('ark_public_settings', publicSettingsData);
		} else {
			localStorage.removeItem('ark_public_settings');
		}
	};

	const setBanksData = (data: any) => {
		const BanksData = JSON.stringify(data);
		if (BanksData !== '') {
			setBanks(data);
			localStorage.setItem('ark_banks', BanksData);
		} else {
			localStorage.removeItem('ark_banks');
		}
	};

	const setUsersData = useCallback(
		async (data: any, canSeeClientNames: boolean) => {
			if (canSeeClientNames !== true) {
				data = data.map((user: any) => {
					user.userName = '*';
					user.firstName = '*';
					user.lastName = '*';
					return user;
				});
			}
			try {
				if (data && data.length > 0) {
					await removeAllUsers(db);
					setUsers(data);
					const userMap = new Map();
					for (const user of data) {
						userMap.set(user.id, user);
					}
					setUsersHMap(userMap);
					await storeUsers(db, data);
				} else {
					await removeAllUsers(db);
				}
			} catch (e) {
				console.log('error===', e);
			}
		},
		[db],
	);
	const setCurrencyPolicyDetails = useCallback(
		async (data: any) => {
			try {
				const currencyPolicyMap: { [key: number]: ICurrencyPolicyProps } = {};
				if (data && data.length > 0) {
					for (const currencyPolicy of data) {
						currencyPolicyMap[currencyPolicy.currencyId] = currencyPolicy;
					}
					setCurrencyPolicies((prev) => ({ ...prev, ...currencyPolicyMap })); // Preserve previous policies
					await storeCurrencyPolicies(db, data);
				} else {
					await removeCurrencyPolicies(db);
					setCurrencyPolicies({});
				}
			} catch (e) {
				console.error('Error while setting currency policies:', e);
			}
		},
		[db],
	);

	const setCurrenciesData = (data: any) => {
		const currenciesData = JSON.stringify(data);
		if (currenciesData !== '') {
			setCurrencies(data);
			localStorage.setItem('ark_currencies', currenciesData); // Fixed key
		} else {
			localStorage.removeItem('ark_currencies'); // Fixed key
		}
	};

	const value = useMemo(
		() => ({
			usersHMap,
			publicSettings,
			setPublicSettingsData,
			users,
			setUsersData,
			banks,
			setBanksData,
			currencies,
			setCurrenciesData,
			currencyPolicies,
			setCurrencyPolicyDetails,
		}),
		[
			usersHMap,
			users,
			banks,
			currencies,
			publicSettings,
			setUsersData,
			currencyPolicies,
			setCurrencyPolicyDetails,
		],
	);

	return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

ConfigContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default ConfigContext;
